'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { Campaign } from '@/types/campaign'
import { Calendar, Clock, Users } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { ReactNode } from 'react'

interface CampaignInfoSummaryProps {
  campaign: Campaign
  badge: ReactNode
}

// Banner + badge + tiêu đề/mô tả + 3 ô thời gian đăng ký/triển khai/đối
// tượng - dùng chung cho màn chọn sản phẩm và màn đăng ký thành công của
// modal đăng ký campaign.
export const CampaignInfoSummary = ({
  campaign,
  badge,
}: CampaignInfoSummaryProps) => {
  const t = useTranslations('community_threads.campaign.register_modal')

  return (
    <div className='grid grid-cols-1 gap-3 md:grid-cols-12 md:items-start md:gap-5'>
      <div className='flex gap-3 md:contents'>
        <div className='relative aspect-[3/4] w-[140px] shrink-0 overflow-hidden rounded-2xl bg-gray-200 md:col-span-4 md:row-span-2 md:aspect-auto md:h-full md:w-full md:self-stretch'>
          <Image
            src={ImageHelper.getN7ImageUrl(campaign.banner)}
            alt={campaign.title}
            fill
            className='object-cover'
          />
        </div>
        <div className='min-w-0 flex-1 md:col-span-8'>
          {badge}
          <h3 className='mt-3 font-criteria text-sm font-bold leading-tight text-neutral-900 md:text-xl'>
            {campaign.title}
          </h3>
          <p
            className='mt-2 text-xs leading-relaxed text-neutral-600 md:text-base'
            dangerouslySetInnerHTML={{ __html: campaign.description }}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 gap-2 md:col-span-8 md:col-start-5 md:grid-cols-2'>
        <div className='flex items-start gap-2 rounded-lg border border-neutral-300 bg-neutral-50 p-3'>
          <Calendar size={20} className='mt-0.5 shrink-0' />
          <div>
            <p className='text-sm font-medium text-neutral-900 md:text-base'>
              {t('register_time_label')}
            </p>
            <p className='text-xs text-neutral-700 md:text-base'>
              {campaign.registerStart} - {campaign.registerEnd}
            </p>
          </div>
        </div>
        <div className='flex items-start gap-2 rounded-lg border border-neutral-300 bg-neutral-50 p-3'>
          <Clock size={20} className='mt-0.5 shrink-0' />
          <div>
            <p className='text-sm font-medium text-neutral-900 md:text-base'>
              {t('deploy_time_label')}
            </p>
            <p className='text-xs text-neutral-700 md:text-base'>
              {campaign.deployStart} - {campaign.deployEnd}
            </p>
          </div>
        </div>
        <div className='flex items-start gap-2 rounded-lg border border-neutral-300 bg-neutral-50 p-3 md:col-span-2'>
          <Users size={20} className='mt-0.5 shrink-0' />
          <div>
            <p className='text-sm font-medium text-neutral-900 md:text-base'>
              {t('target_label')}
            </p>
            <p className='text-xs text-neutral-700 md:text-base'>
              {campaign.target}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
